import { z } from 'zod';
import { addDaysLocal, type LocalDate } from '../time';
import { idSchema, localDateSchema, versionSchema } from './common';
import { VAULT_STEP_UP_MAX_AGE_SEC } from './identity';
import { CREDENTIAL_MASK, credentialFieldsSchema, credentialSecretSchema } from './maintenance';

/**
 * Sejf dostępów (MODULE_UTRZYMANIE §4, PERMISSIONS §3.3 „Widzi dostępy”, DESIGN §5 „Sejf dostępów”, Figma 08
 * „Pokaż dostęp”): `credentials` (packages/db/src/schema/maintenance.ts) + `vault_rotation_reminders`.
 *
 * - Sekret tylko jako szyfrogram AES-256-GCM (`secret_ciphertext`, `secret_iv`, `secret_tag`, `key_version`; DEK
 *   workspace'u, AAD = `workspace_id:credentials:id`; `encryptSecret` / `decryptSecret` z @fos/core). Nigdy w listach,
 *   logach, eksportach, audycie, kontekście AI ani MCP; lista nie zdradza długości (stała maska `CREDENTIAL_MASK`).
 * - Podgląd i kopiowanie: `ctx.access.assertVault(assetId)` + świeży kod z maila (Fala 7: `requireStepUp(ctx,
 *   { kind: 'second_factor', maxAgeSec: VAULT_STEP_UP_MAX_AGE_SEC })`, 5 min), jeden sekret na żądanie, widoczny
 *   30 s, audyt `vault.reveal` / `vault.copy` (kto, kiedy, który dostęp, urządzenie), `last_revealed_at/_by` na rekordzie. AI i MCP: FORBIDDEN (`denyVia`), narzędzia MCP
 *   mogą najwyżej powiedzieć, że dostęp istnieje (`hasSecret`).
 * - Przypomnienia o zmianie hasła: po odejściu osoby z dostępem (`member_left`) i co 12 miesięcy (`yearly`).
 * Wejścia i wyjścia usług dopisuje agent modułu vault.
 */

export { CREDENTIAL_MASK, VAULT_STEP_UP_MAX_AGE_SEC };

/** Jak długo sekret jest widoczny po podglądzie (UI chowa go po tym czasie; ponowny podgląd = nowy step-up i audyt). */
export const VAULT_REVEAL_VISIBLE_SEC = 30;

/** Limit odczytów sekretów (podgląd + kopia z serwera) na osobę w workspace: 20 na godzinę. */
export const VAULT_REVEAL_RATE_LIMIT = 20;
export const VAULT_REVEAL_RATE_WINDOW_SEC = 60 * 60;

/** Przypomnienie roczne powstaje (zadanie `vault.rotation.yearly`), gdy do terminu zostało najwyżej tyle dni. */
export const VAULT_YEARLY_REMINDER_LEAD_DAYS = 30;

/** Kolejki workera sejfu (CONVENTIONS §8). */
export const VAULT_QUEUES = {
  reencrypt: 'vault.reencrypt',
  yearly: 'vault.rotation.yearly',
} as const;

/** Cel odczytu sekretu (osobne wpisy audytu). */
export const VAULT_REVEAL_PURPOSES = ['reveal', 'copy'] as const;
export type VaultRevealPurpose = (typeof VAULT_REVEAL_PURPOSES)[number];

/** Akcje audytu sejfu (bez wartości sekretu; `meta`: urządzenie, cel). */
export const VAULT_AUDIT_ACTIONS = {
  create: 'credential.create',
  update: 'credential.update',
  secretChange: 'credential.secret_change',
  archive: 'credential.archive',
  restore: 'credential.restore',
  reveal: 'vault.reveal',
  copy: 'vault.copy',
  rotationResolve: 'vault.rotation.resolve',
  rotationCreate: 'vault.rotation.create',
  keysRotate: 'vault.keys.rotate',
  keysReencrypt: 'vault.keys.reencrypt',
} as const;

// ── Przypomnienia o zmianie hasła ───────────────────────────────────────────

export const VAULT_ROTATION_REASONS = ['member_left', 'yearly'] as const;
export type VaultRotationReason = (typeof VAULT_ROTATION_REASONS)[number];
export const vaultRotationReasonSchema = z.enum(VAULT_ROTATION_REASONS);

/** Zamknięcie przypomnienia: hasło zmienione albo świadomie pominięte. */
export const VAULT_ROTATION_RESOLUTIONS = ['changed', 'dismissed'] as const;
export type VaultRotationResolution = (typeof VAULT_ROTATION_RESOLUTIONS)[number];
export const vaultRotationResolutionSchema = z.enum(VAULT_ROTATION_RESOLUTIONS);

/** Przypomnienie roczne: 12 miesięcy od ostatniej zmiany sekretu (`secret_changed_at`). */
export const VAULT_YEARLY_ROTATION_MONTHS = 12;
/** Po odejściu osoby z dostępem: zmiana w ciągu 7 dni (termin przypomnienia). */
export const VAULT_MEMBER_LEFT_DUE_DAYS = 7;

/** Termin przypomnienia po odejściu osoby (data dezaktywacji + 7 dni). */
export function memberLeftRotationDueOn(leftOn: LocalDate): LocalDate {
  return addDaysLocal(leftOn, VAULT_MEMBER_LEFT_DUE_DAYS);
}

/** Termin przypomnienia rocznego: ta sama data rok po zmianie sekretu (29.02 → 28.02). */
export function yearlyRotationDueOn(changedOn: LocalDate): LocalDate {
  const [y, m, d] = changedOn.split('-').map(Number) as [number, number, number];
  const year = y + VAULT_YEARLY_ROTATION_MONTHS / 12;
  const last = new Date(Date.UTC(year, m, 0)).getUTCDate();
  return `${year}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

// ── Wejścia usług (router `vault`) ──────────────────────────────────────────

export const listCredentialsInput = z.object({
  assetId: idSchema,
  /** także dostępy w archiwum (domyślnie bez) */
  includeArchived: z.boolean().default(false),
});

/** Nowy dostęp: pola + opcjonalny sekret (albo link do menedżera haseł w `externalRef`). */
export const createCredentialInput = credentialFieldsSchema.extend({
  secret: credentialSecretSchema.optional(),
});
export type CreateCredentialInput = z.input<typeof createCredentialInput>;

/**
 * Zmiana dostępu: `secret` pominięty = bez zmiany, string = nowy sekret (`secret_changed_at`, zamyka otwarte
 * przypomnienia o zmianie), `null` = usuń sekret (zostaje login / link). Zmiana sekretu wymaga step-up (10 min).
 */
export const updateCredentialInput = credentialFieldsSchema.omit({ assetId: true }).partial().extend({
  id: idSchema,
  version: versionSchema,
  secret: credentialSecretSchema.nullable().optional(),
});
export type UpdateCredentialInput = z.input<typeof updateCredentialInput>;

export const credentialRefInput = z.object({ id: idSchema });

/** Podgląd albo kopia jednego sekretu (step-up ≤ 60 s). */
export const revealCredentialInput = z.object({
  id: idSchema,
  purpose: z.enum(VAULT_REVEAL_PURPOSES).default('reveal'),
});

/** Kopia sekretu już widocznego po podglądzie (kopiowanie w przeglądarce; tylko wpis `vault.copy` w audycie). */
export const logCopyInput = credentialRefInput;

/** Rotacja klucza danych (DEK) workspace'u: nowa wersja + przeszyfrowanie sekretów w tle. */
export const rotateKeysInput = z.object({}).strict().default({});

export const listRotationRemindersInput = z
  .object({
    assetId: idSchema.optional(),
    /** tylko otwarte (bez `done_at`) */
    openOnly: z.boolean().default(true),
    dueBefore: localDateSchema.optional(),
  })
  .default({});

export const resolveRotationReminderInput = z.object({
  id: idSchema,
  resolution: vaultRotationResolutionSchema,
});

// ── Wyjścia (DTO) — nigdy szyfrogram, IV, tag ani długość sekretu ────────────

export interface CredentialDto {
  id: string;
  version: number;
  assetId: string;
  label: string;
  panelUrl: string | null;
  login: string | null;
  /** czy jest zapisany sekret (maska `CREDENTIAL_MASK` o stałej długości) */
  hasSecret: boolean;
  externalRef: string | null;
  note: string | null;
  secretChangedAt: Date | null;
  lastRevealedAt: Date | null;
  /** najbliższe otwarte przypomnienie o zmianie hasła */
  rotationDue: { id: string; reason: VaultRotationReason; dueOn: LocalDate } | null;
  /** osoba może podejrzeć / skopiować (sejf + zakres + sekret zapisany); i tak wymaga step-up */
  canReveal: boolean;
  archivedAt: Date | null;
}

/**
 * Lista dostępów zasobu (`vault.list`). Bez dostępu do sejfu (`access: 'restricted'`) lista jest pusta i nie zdradza
 * nawet liczby dostępów (MODULE_UTRZYMANIE §7 nr 5: Utrzymanie = Podgląd bez „Widzi dostępy”).
 */
export interface CredentialListDto {
  access: 'full' | 'restricted';
  /** może dodawać, zmieniać i archiwizować dostępy (sejf + Utrzymanie = Edycja + edycja zasobu) */
  canManage: boolean;
  items: CredentialDto[];
}

export interface CredentialRevealResult {
  id: string;
  /** JEDEN jawny sekret; UI trzyma go tylko w stanie komponentu i czyści po `revealedUntil` */
  secret: string;
  /** do kiedy UI pokazuje sekret (teraz + `VAULT_REVEAL_VISIBLE_SEC`) */
  revealedUntil: Date;
}

export interface RotateKeysResult {
  /** nowa aktywna wersja klucza danych */
  keyVersion: number;
  /** sekrety do przeszyfrowania w tle (zadanie `vault.reencrypt`); do tego czasu odczyt starszą wersją */
  pending: number;
}

export interface ReencryptResult {
  keyVersion: number;
  reencrypted: number;
  /** szyfrogramy, których nie dało się odszyfrować (zostają bez zmian) */
  failed: number;
  remaining: number;
}

export interface VaultRotationReminderDto {
  id: string;
  credentialId: string;
  credentialLabel: string;
  assetId: string;
  assetName: string;
  reason: VaultRotationReason;
  /** osoba, która odeszła (`member_left`) */
  member: { userId: string; name: string } | null;
  dueOn: LocalDate;
  doneAt: Date | null;
  resolution: VaultRotationResolution | null;
}

export const VAULT_MESSAGES = {
  noAccess: 'Nie masz dostępu do sejfu tego zasobu',
  aiDenied: 'AI i MCP nie mają dostępu do sejfu dostępów',
  noSecret: 'Ten dostęp nie ma zapisanego hasła',
  notFound: 'Nie znaleziono dostępu',
  archived: 'Ten dostęp jest w archiwum',
  rateLimited: 'Za dużo podglądów haseł w ciągu godziny. Spróbuj ponownie później.',
  ownerOnly: 'Klucze sejfu zmienia tylko właściciel albo administrator',
  decryptFailed: 'Nie udało się odszyfrować hasła',
  restricted: 'Dostępy: ograniczone',
  restrictedHint: 'Hasła widzą osoby z uprawnieniem „Widzi dostępy”.',
  revealNote: 'Podgląd zapiszemy w historii zasobu: kto, kiedy, który dostęp.',
  codeNote: 'Kod wyślemy mailem. Po potwierdzeniu przez 5 minut nie poprosimy o kolejny.',
  footer:
    'Zaszyfrowane. Podgląd i kopiowanie wymagają potwierdzenia i trafiają do historii. AI nie ma do nich dostępu.',
} as const;
